import React, { ChangeEvent, memo, ReactNode } from 'react'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { Radio } from '../Radio'

const useRadioGroupStyles = makeStyles({
  invalid: {
    color: brandColors.error,
  },
})

export interface RadioGroupOption {
  id?: string
  label?: string | ReactNode
  name?: string
  value: string
  checked?: boolean
  disabled?: boolean
  dataTest?: string
}

export interface IRadioGroupProps {
  label?: string
  name: string
  disabled?: boolean
  checked?: string | number | boolean | null
  options: RadioGroupOption[]
  stacked?: boolean
  legacyLabel?: boolean
  required?: boolean
  invalid?: boolean
  validationMessage?: string
  dataTest?: string
  onOptionChange: (event: ChangeEvent<any>, checked: boolean) => void
}

export const RadioGroup = memo<IRadioGroupProps>(
  ({
    label = 'Choose an option',
    checked = '',
    disabled = false,
    stacked = false,
    legacyLabel = false,
    required = true,
    invalid = false,
    options = [],
    name,
    dataTest,
    validationMessage,
    onOptionChange = () => {},
  }) => {
    const styles = useRadioGroupStyles()
    return (
      <fieldset disabled={disabled} data-test={dataTest}>
        <legend
          className={`label ${required ? 'required' : ''} ${legacyLabel ? 'text-small' : ''}`}>
          {label}
        </legend>
        <div className={stacked ? '' : 'flex'}>
          {options.map(option => (
            <Radio
              name={name}
              disabled={disabled || option.disabled}
              onChange={onOptionChange}
              id={option.id}
              dataTest={option.dataTest}
              key={option.value}
              label={option.label}
              value={option.value}
              checked={checked === option.value}
              validationMessage={validationMessage}
              className='mr2'
            />
          ))}
          {invalid && (
            <span data-test={`${dataTest}-message`} className={styles.invalid}>
              {validationMessage}
            </span>
          )}
        </div>
      </fieldset>
    )
  }
)

export default RadioGroup
